/**
 * Global Navigation Service
 * Provides intelligent back navigation and common navigation patterns
 */

import { useRouter, useRoute } from 'vue-router'

export function useSmartNavigation() {
  const router = useRouter()
  const route = useRoute()

  /**
   * Smart back navigation that considers context and referrer
   * @param {Object} options - Navigation options
   * @param {string} options.fallback - Fallback route if no history
   * @param {Object} options.fallbackParams - Parameters for fallback route
   * @param {Object} options.fallbackQuery - Query parameters for fallback route
   * @param {boolean} options.preserveQuery - Whether to preserve current query parameters
   */
  const goBack = (options = {}) => {
    const {
      fallback = '/',
      fallbackParams = {},
      fallbackQuery = {},
      preserveQuery = false
    } = options


    if (window.history.length > 1) {

      const referrer = document.referrer
      const currentOrigin = window.location.origin
      
      if (referrer.startsWith(currentOrigin)) {
        const referrerPath = new URL(referrer).pathname
        const currentPath = route.path
        

        if (referrerPath !== currentPath) {
          router.go(-1)
          return
        }
      }
      

      const query = route.query
      

      if (query.from === 'team' && query.teamId) {
        const targetQuery = preserveQuery ? { ...query } : {}
        delete targetQuery.from 
        
        router.push({
          name: 'teams-detail',
          params: { id: query.teamId },
          query: Object.keys(targetQuery).length > 0 ? targetQuery : undefined
        })
        return
      }
      

      if (query.from && query.returnTo) {
        router.push(query.returnTo)
        return
      }
      

      router.go(-1)
    } else {

      router.push({
        name: fallback,
        params: fallbackParams,
        query: Object.keys(fallbackQuery).length > 0 ? fallbackQuery : undefined
      })
    }
  }

  /**
   * Navigate back to team detail page
   * @param {string} teamId - Team ID to navigate to
   */
  const goBackToTeam = (teamId) => {
    if (teamId) {
      router.push({
        name: 'teams-detail',
        params: { id: teamId }
      })
    } else {
      router.push({ name: 'teams-list' })
    }
  }

  /**
   * Navigate back to teams list
   */
  const goBackToTeamsList = () => {
    router.push({ name: 'teams-list' })
  }

  /**
   * Navigate back to receipts list with optional context
   * @param {Object} context - Navigation context
   */
  const goBackToReceipts = (context = {}) => {
    const query = { ...context }
    

    if (route.query.teamId && !query.teamId) {
      query.teamId = route.query.teamId
    }
    
    router.push({
      name: 'receipts',
      query: Object.keys(query).length > 0 ? query : undefined
    })
  }

  /**
   * Navigate with context preservation
   * @param {Object} destination - Navigation destination
   * @param {Object} context - Context to add to navigation
   */
  const navigateWithContext = (destination, context = {}) => {
    const { name, params = {}, query = {} } = destination
    

    const finalQuery = {
      ...query,
      ...context
    }
    
    router.push({
      name,
      params,
      query: Object.keys(finalQuery).length > 0 ? finalQuery : undefined
    })
  }

  /**
   * Get current navigation context
   */
  const getNavigationContext = () => {
    return {
      from: route.query.from,
      teamId: route.query.teamId,
      returnTo: route.query.returnTo,
      currentRoute: route.name,
      currentParams: route.params,
      currentQuery: route.query
    }
  }

  return {
    goBack,
    goBackToTeam,
    goBackToTeamsList,
    goBackToReceipts,
    navigateWithContext,
    getNavigationContext
  }
}

/**
 * Navigation context constants
 */
export const NavigationContext = {
  FROM_TEAM: 'team',
  FROM_RECEIPTS: 'receipts',
  FROM_ADMIN: 'admin'
}

/**
 * Common fallback routes
 */
export const FallbackRoutes = {
  HOME: 'home',
  DASHBOARD: 'dashboard',
  TEAMS: 'teams-list',
  RECEIPTS: 'receipts'
} 